import client from "./client";

export interface WireGuardServer {
  code: string;
  name: string;
  endpoint: string;
  is_default: boolean;
}

export type VlessServerMode = "auto" | "de" | "nl" | "us" | "jammer" | "eu1";
export type VpnJobStatus = "queued" | "processing" | "done" | "error";

export interface VpnJob {
  id: string;
  target: "wireguard" | "vless";
  server?: string | null;
  action: "generate" | "refresh";
  status: VpnJobStatus;
  attempts?: number;
  result?: { code?: string; finished_at?: string | null } | null;
}

export interface VlessPreferencesResponse {
  success: boolean;
  server_mode: VlessServerMode;
  available_modes?: VlessServerMode[];
}

export interface CustomVlessLinkResponse extends Partial<VlessPreferencesResponse> {
  success: boolean;
  message?: string;
  job?: VpnJob;
  provisioning_enabled?: boolean;
  subscription_url?: string;
  short_uuid?: string;
  expires_at?: string | null;
  device_status?: {
    state?: "not_linked" | "linked" | "blocked";
    device_count?: number;
    strong_device_count?: number;
    device_limit?: number;
    warning_level?: "none" | "limit_reached" | "final_warning" | "blocked";
    warning_message?: string;
    rotation_required?: boolean;
    strong_control_ready?: boolean;
  };
  device_state?: "not_linked" | "linked" | "blocked";
  device_count?: number;
  device_limit?: number;
  device_warning_level?: "none" | "limit_reached" | "final_warning" | "blocked";
  device_warning_message?: string;
  rotation_required?: boolean;
  profile_version?: number;
}

export interface ResetV2DevicesResponse {
  success: boolean;
  message?: string;
  subscription_url?: string;
  short_uuid?: string;
  devices_disconnected?: number;
  happ_only?: boolean;
  device_limit?: number;
}

export interface WireGuardOperationResponse {
  success: boolean;
  server?: string;
  config?: string;
  existing?: boolean;
  message?: string;
  job?: VpnJob;
  provisioning_enabled?: boolean;
}

export interface VpnPollOptions {
  signal?: AbortSignal;
  onStatus?: (job: VpnJob, pollNumber: number) => void;
  pollDelaysMs?: readonly number[];
  idempotencyKey?: string;
}

export type WireGuardProvisionResult =
  | { state: "ready"; config: string; response: WireGuardOperationResponse }
  | { state: "pending"; job: VpnJob; response: WireGuardOperationResponse };

// Provisioning is handled by a durable worker which can start on the next
// scheduler tick. Keep watching long enough for a full one-minute tick plus
// node processing instead of making the user press the button again.
export const WIREGUARD_PROVISION_POLL_DELAYS_MS = [
  1_000, 2_000, 3_000, 4_000,
  5_000, 5_000, 5_000, 5_000, 5_000, 5_000, 5_000,
  5_000, 5_000, 5_000, 5_000, 5_000, 5_000, 5_000,
] as const;

const DEFAULT_POLL_DELAYS_MS = WIREGUARD_PROVISION_POLL_DELAYS_MS;

export async function getWireGuardServers(signal?: AbortSignal): Promise<WireGuardServer[]> {
  const { data } = await client.get<{ success: boolean; servers?: WireGuardServer[] }>(
    "/vpn/wireguard.php?action=servers",
    { signal }
  );
  return data.success && Array.isArray(data.servers) ? data.servers : [];
}

export async function getWireGuardConfig(server: string, signal?: AbortSignal): Promise<string | null> {
  const { data } = await client.get<{ success: boolean; config?: string }>(
    `/vpn/wireguard.php?action=get_config&server=${encodeURIComponent(server)}`,
    { signal }
  );
  return data.success ? data.config ?? null : null;
}

export async function requestWireGuardProfile(
  server: string,
  action: "generate" | "refresh",
  idempotencyKey: string,
  signal?: AbortSignal
): Promise<WireGuardOperationResponse> {
  const { data } = await client.post<WireGuardOperationResponse>(
    "/vpn/wireguard.php",
    action === "refresh" ? { server, action } : { server },
    {
      signal,
      timeout: 15_000,
      headers: { "Idempotency-Key": idempotencyKey },
    }
  );
  return data;
}

export async function provisionWireGuardProfile(
  server: string,
  action: "generate" | "refresh",
  options: VpnPollOptions = {}
): Promise<WireGuardProvisionResult> {
  const idempotencyKey = options.idempotencyKey ?? createVpnIdempotencyKey(action);
  const delays = options.pollDelaysMs ?? DEFAULT_POLL_DELAYS_MS;
  let response = await requestWireGuardProfile(server, action, idempotencyKey, options.signal);

  for (let pollNumber = 0; ; pollNumber += 1) {
    if (!response.success) {
      throw new Error(response.message || "VPN profile request failed");
    }
    if (response.config) {
      return { state: "ready", config: response.config, response };
    }
    const job = response.job;
    if (!job) {
      throw new Error(response.message || "VPN job state is missing");
    }
    options.onStatus?.(job, pollNumber);
    if (job.status === "error") {
      throw new Error(response.message || "VPN profile provisioning failed");
    }
    if (job.status === "done") {
      const config = await getWireGuardConfig(server, options.signal);
      if (config) return { state: "ready", config, response };
      throw new Error("VPN profile finished without a configuration");
    }
    if (pollNumber >= delays.length) {
      return { state: "pending", job, response };
    }

    await abortableDelay(delays[pollNumber], options.signal);
    response = await requestWireGuardProfile(server, action, idempotencyKey, options.signal);
  }
}

/** Compatibility wrappers for callers which need a single raw request. */
export function generateWireGuardConfig(
  server: string,
  signal?: AbortSignal
): Promise<WireGuardOperationResponse> {
  return requestWireGuardProfile(server, "generate", createVpnIdempotencyKey("generate"), signal);
}

export function refreshWireGuardConfig(
  server: string,
  signal?: AbortSignal
): Promise<WireGuardOperationResponse> {
  return requestWireGuardProfile(server, "refresh", createVpnIdempotencyKey("refresh"), signal);
}

export async function downloadWireGuardConfig(server: string, signal?: AbortSignal): Promise<Blob> {
  const response = await client.get(
    `/vpn/wireguard.php?action=download&server=${encodeURIComponent(server)}`,
    { responseType: "blob", signal }
  );
  return response.data as Blob;
}

export async function downloadWireGuardArchive(signal?: AbortSignal): Promise<Blob> {
  const response = await client.get("/vpn/wireguard_archive.php", {
    responseType: "blob",
    signal,
    timeout: 30_000,
  });
  return response.data as Blob;
}

export async function getVlessPreferences(): Promise<VlessPreferencesResponse> {
  const { data } = await client.get<VlessPreferencesResponse>("/vpn/vless_preferences.php");
  return data;
}

export async function setVlessPreferences(serverMode: VlessServerMode): Promise<VlessPreferencesResponse> {
  const { data } = await client.post<VlessPreferencesResponse>("/vpn/vless_preferences.php", {
    server_mode: serverMode,
  });
  return data;
}

export async function requestCustomVlessLink(
  idempotencyKey: string,
  signal?: AbortSignal
): Promise<CustomVlessLinkResponse> {
  const { data } = await client.get<CustomVlessLinkResponse>("/vpn/custom_vless_link.php", {
    signal,
    headers: {
      "Idempotency-Key": idempotencyKey,
      "X-App-Device-Id": getOrCreateAppDeviceId(),
    },
  });
  return data;
}

export async function getCustomVlessLink(
  options: VpnPollOptions = {}
): Promise<CustomVlessLinkResponse> {
  const idempotencyKey = options.idempotencyKey ?? createVpnIdempotencyKey("vless");
  const delays = options.pollDelaysMs ?? DEFAULT_POLL_DELAYS_MS;
  let response = await requestCustomVlessLink(idempotencyKey, options.signal);

  for (let pollNumber = 0; ; pollNumber += 1) {
    if (!response.success) return response;
    if (response.subscription_url) return response;
    const job = response.job;
    if (!job) return response;
    options.onStatus?.(job, pollNumber);
    if (job.status === "error" || pollNumber >= delays.length) return response;

    await abortableDelay(delays[pollNumber], options.signal);
    response = await requestCustomVlessLink(idempotencyKey, options.signal);
  }
}

export async function resetV2Devices(signal?: AbortSignal): Promise<ResetV2DevicesResponse> {
  const { data } = await client.post<ResetV2DevicesResponse>(
    "/vpn/reset_v2_devices.php",
    { confirm: true },
    { signal, timeout: 15_000 }
  );
  return data;
}

function createVpnIdempotencyKey(scope: string): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === "function") {
    return `vpn-${scope}-${cryptoApi.randomUUID()}`;
  }

  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  return `vpn-${scope}-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

function abortableDelay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(createAbortError());

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, Math.max(0, milliseconds));
    const onAbort = () => {
      window.clearTimeout(timeout);
      signal?.removeEventListener("abort", onAbort);
      reject(createAbortError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function createAbortError(): DOMException {
  return new DOMException("VPN request cancelled", "AbortError");
}

function getOrCreateAppDeviceId(): string {
  const key = "simolapps_vless_device_id";
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;

  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  const value = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  window.localStorage.setItem(key, value);
  return value;
}
